import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { downloadDatabookQnaExcel, getDatabookInsights, type DatabookInsights } from "@/lib/api/databook";
import { getQnaQuestions } from "@/lib/api/fddQa";
import { BackendError } from "@/lib/backend";
import { DatabookVariancePanel } from "./DatabookVariancePanel";
import { DatabookQnaPanel } from "./DatabookQnaPanel";
import { DatabookKeyCommentsInspector } from "./DatabookKeyCommentsInspector";
import {
  nextInsightPosition,
  previousInsightPosition,
  insightPosition,
  type InsightCursor,
} from "./databookInsightNavigation";

const INSIGHT_SECTIONS = [
  { value: "qna", label: "Q&A" },
  { value: "variances", label: "Variances" },
  { value: "key-comments", label: "Key Takeaways" },
] as const;

export type DatabookInsightKind = (typeof INSIGHT_SECTIONS)[number]["value"];

export interface DatabookInsightsInspectorProps {
  insights?: DatabookInsights;
  kind: DatabookInsightKind;
  onKindChange: (kind: DatabookInsightKind) => void;
  projectId?: string;
  versionId?: string;
  previewRevision: number;
  sheetName: string | null;
  sheetNames: string[];
  sheetListError: boolean;
  onSelectSheet: (sheetName: string) => void;
  onOpenQnaQuestion?: (questionId: string) => void;
  onRetryWorkbook: () => void;
  onClose: () => void;
}

export function DatabookInsightsInspector(props: DatabookInsightsInspectorProps) {
  return (
    <Tabs value={props.kind} onValueChange={(value) => {
      const section = INSIGHT_SECTIONS.find(section => section.value === value);
      if (section) props.onKindChange(section.value);
    }} asChild>
      <aside id="databook-insights-panel" aria-label="Databook insights" className="flex h-full min-w-0 flex-col bg-card">
        <header className="flex shrink-0 items-start justify-between gap-2 border-b border-border px-3 py-3">
          <TabsList aria-label="Insight sections" className="h-auto min-w-0 flex-1 flex-wrap justify-start gap-0.5 p-0.5">
            {INSIGHT_SECTIONS.map(section => (
              <TabsTrigger key={section.value} value={section.value} className="px-2 py-1.5 text-[11px]">
                {section.label}
              </TabsTrigger>
            ))}
          </TabsList>
          <button type="button" aria-label="Close insights" onClick={props.onClose}
            className="mt-0.5 shrink-0 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <X aria-hidden="true" size={16} />
          </button>
        </header>
        <TabsContent value={props.kind} className="mt-0 min-h-0 flex-1">
          {props.kind === "key-comments"
            ? <DatabookKeyCommentsInspector {...props} />
            : <SheetInsightsInspector key={props.kind} {...props} />}
        </TabsContent>
      </aside>
    </Tabs>
  );
}

function SheetInsightsInspector({
  insights,
  kind, projectId, versionId, previewRevision, sheetName, sheetNames, sheetListError,
  onSelectSheet, onRetryWorkbook,
}: DatabookInsightsInspectorProps) {
  const varianceQuery = useQuery({
    queryKey: ["databook-insights", projectId, versionId, previewRevision],
    queryFn: ({ signal }) => getDatabookInsights(projectId!, versionId!, signal),
    enabled: !insights && kind === "variances" && Boolean(projectId && versionId),
    staleTime: Infinity,
    retry: false,
  });
  const qnaQuery = useQuery({
    queryKey: ["databook-qna-register", projectId],
    queryFn: ({ signal }) => getQnaQuestions(projectId!, signal),
    enabled: !insights && kind === "qna" && Boolean(projectId),
    retry: false,
  });
  const query = kind === "qna" ? qnaQuery : varianceQuery;
  const questions = insights?.questions
    ?? qnaQuery.data?.questions.filter(question => question.status !== "Rejected")
    ?? [];
  const variances = insights?.variances ?? varianceQuery.data?.variances ?? [];
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const downloadQna = async () => {
    if (!projectId || downloading) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      await downloadDatabookQnaExcel(projectId);
    } catch {
      setDownloadError("Q&A could not be downloaded. Please try again.");
    } finally {
      setDownloading(false);
    }
  };
  const [cursor, setCursor] = useState<InsightCursor | null>(null);
  if (cursor && cursor.sheetName !== sheetName) setCursor(null);
  const entrySheets = kind === "qna"
    ? questions.map(question => question.databook_tab)
    : variances.map(finding => finding.sheet_name);
  const position = insightPosition(entrySheets, sheetName, cursor);
  const previousPosition = previousInsightPosition(entrySheets, sheetNames, sheetName, position);
  const navigate = (index: number) => {
    const targetSheet = entrySheets[index];
    setCursor({ sheetName: targetSheet ?? sheetName, position: index });
    if (targetSheet && targetSheet !== sheetName) onSelectSheet(targetSheet);
  };
  const staleVersion = query.error instanceof BackendError && query.error.status === 409;
  const panelProps = {
    sheetName: sheetName ?? "Databook",
    position,
    total: entrySheets.length,
    loading: !insights && !sheetListError && query.isPending,
    error: sheetListError || (!insights && query.isError),
    onRetry: () => {
      if (sheetListError || staleVersion || !versionId) onRetryWorkbook();
      else void query.refetch();
    },
    onNext: () => navigate(position === entrySheets.length
      ? 0 : nextInsightPosition(entrySheets, sheetNames, sheetName, position)),
    onPrevious: () => navigate(previousPosition),
    previousDisabled: previousPosition < 0,
  };
  return kind === "qna"
    ? <DatabookQnaPanel {...panelProps} onDownload={() => void downloadQna()} downloading={downloading} downloadError={downloadError} question={questions[position] ?? null} />
    : <DatabookVariancePanel {...panelProps} finding={variances[position] ?? null} />;
}
